import React from 'react';
import { useTranslation } from 'react-i18next';

interface LoadingPageProps {
  fullScreen?: boolean;
  message?: string;
}

const LoadingPage: React.FC<LoadingPageProps> = ({ fullScreen = true, message }) => {
  const { t } = useTranslation();

  const containerClass = fullScreen
    ? 'min-h-screen flex items-center justify-center'
    : 'flex items-center justify-center py-12';

  const loadingMessage = message || t('loading.defaultMessage', 'Loading...');

  return (
    <div className={containerClass}>
      <div className="flex flex-col items-center justify-center space-y-6">
        {/* Animated Logo/Spinner */}
        <div className="relative w-16 h-16">
          {/* Outer ring */}
          <div className="absolute inset-0 border-4 border-slate-100 rounded-full" />
          {/* Animated blue segment */}
          <div className="absolute inset-0 border-4 border-blue-600 rounded-full border-t-transparent border-r-transparent animate-spin" />
          {/* Inner dot */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-2 h-2 bg-blue-600 rounded-full" />
          </div>
        </div>

        {/* Loading Message */}
        <div className="text-center">
          <p className="text-slate-600 font-medium">{loadingMessage}</p>
          <p className="text-slate-400 text-sm mt-1">
            {t('loading.pleaseWait', 'Please wait')}
          </p>
        </div>

        {/* Animated dots */}
        <div className="flex space-x-1">
          <div
            className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"
            style={{ animationDelay: '0ms' }}
          />
          <div
            className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
            style={{ animationDelay: '150ms' }}
          />
          <div
            className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"
            style={{ animationDelay: '300ms' }}
          />
        </div>
      </div>
    </div>
  );
};

// Compact inline loading spinner for smaller spaces
export const LoadingSpinner: React.FC<{ size?: 'sm' | 'md' | 'lg' }> = ({ size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4 border-2',
    md: 'w-6 h-6 border-2',
    lg: 'w-8 h-8 border-3',
  };

  return (
    <div className="flex items-center justify-center">
      <div
        className={`${sizeClasses[size]} border-slate-200 border-t-blue-600 rounded-full animate-spin`}
      />
    </div>
  );
};

// Skeleton loader for content placeholders
export const Skeleton: React.FC<{
  className?: string;
  variant?: 'text' | 'circle' | 'rect';
}> = ({ className = '', variant = 'rect' }) => {
  const baseClasses = 'bg-slate-200 animate-pulse';
  const variantClasses = {
    text: 'h-4 rounded',
    circle: 'rounded-full',
    rect: 'rounded-lg',
  };

  return <div className={`${baseClasses} ${variantClasses[variant]} ${className}`} />;
};

// Card skeleton for job cards
export const JobCardSkeleton: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="bg-white rounded-xl shadow-sm p-5 border border-slate-100">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <Skeleton className="h-6 w-3/4 mb-2" variant="text" />
          <Skeleton className="h-4 w-1/2" variant="text" />
        </div>
        <Skeleton className="w-16 h-6" variant="rect" />
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Skeleton className="w-20 h-6" variant="rect" />
        <Skeleton className="w-16 h-6" variant="rect" />
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between">
        <Skeleton className="w-24 h-4" variant="text" />
        <Skeleton className="w-20 h-8" variant="rect" />
      </div>
    </div>
  );
};

// Loading state wrapper for async operations
export const LoadingWrapper: React.FC<{
  isLoading: boolean;
  error?: Error | null;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  errorFallback?: React.ReactNode;
  onRetry?: () => void;
}> = ({ isLoading, error, children, fallback, errorFallback, onRetry }) => {
  const { t } = useTranslation();

  if (error) {
    if (errorFallback) {
      return <>{errorFallback}</>;
    }

    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-red-50 rounded-full mb-4">
          <svg
            className="w-8 h-8 text-red-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          {t('loading.errorTitle', 'Failed to load')}
        </h3>
        <p className="text-slate-600 mb-4">
          {t('loading.errorMessage', 'Please try again later')}
        </p>
        {onRetry && (
          <button
            onClick={onRetry}
            className="px-4 py-2 bg-blue-600 text-white rounded-full font-medium hover:bg-blue-700 transition-all"
          >
            {t('loading.retryBtn', 'Retry')}
          </button>
        )}
      </div>
    );
  }

  if (isLoading) {
    if (fallback) {
      return <>{fallback}</>;
    }
    return <LoadingPage fullScreen={false} />;
  }

  return <>{children}</>;
};

export default LoadingPage;
